// src/services/paymentService.js
// All paths are relative to API_BASE_URL (e.g. https://host/api)
// Backend orders/urls.py payment routes:
//   POST  orders/payments/initiate/          → InitiatePaymentView
//   GET   orders/payments/<pk>/              → PaymentStatusView
//   POST  orders/payments/<id>/process/      → NCBAPaymentView
//   POST  orders/payments/ncba/callback/     → NCBACallbackView
//   POST  orders/payments/ncba/qr-generate/  → NCBAQRGenerationView
//   GET   orders/<order_id>/payment-status/  → OrderPaymentStatusView

import axiosInstance from '../utils/axiosConfig';
import logger from '../utils/logger';

class PaymentService {
  /**
   * Initiate payment for an order
   * POST /api/orders/payments/initiate/
   * Body: { order, amount, payment_method, phone_number? }
   * payment_method: 'ncba' | 'mpesa' | 'cash'
   */
  async initiatePayment(paymentData) {
    try {
      const response = await axiosInstance.post('orders/payments/initiate/', paymentData);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to initiate payment');
    }
  }

  /**
   * Get payment status by payment ID
   * GET /api/orders/payments/<pk>/
   */
  async getPaymentStatus(paymentId) {
    try {
      const response = await axiosInstance.get(`orders/payments/${paymentId}/`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to get payment status');
    }
  }

  /**
   * Get payment status for a specific order
   * GET /api/orders/<order_id>/payment-status/
   */
  async checkPaymentStatus(orderId) {
    try {
      const response = await axiosInstance.get(`orders/${orderId}/payment-status/`);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to check payment status');
    }
  }

  /**
   * Process payment (NCBA)
   * POST /api/orders/payments/<id>/process/
   */
  async processPayment(paymentId, paymentData) {
    try {
      const response = await axiosInstance.post(`orders/payments/${paymentId}/process/`, paymentData);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to process payment');
    }
  }

  /**
   * Generate NCBA QR code
   * POST /api/orders/payments/ncba/qr-generate/
   */
  async generateNCBAQR(data) {
    try {
      const response = await axiosInstance.post('orders/payments/ncba/qr-generate/', data);
      return response.data;
    } catch (error) {
      throw this.handleError(error, 'Failed to generate QR code');
    }
  }

  /**
   * Validate payment data before submission
   */
  validatePaymentData(paymentData) {
    const errors = [];
    if (!paymentData.amount || paymentData.amount <= 0) errors.push('Amount must be greater than 0');
    if (!paymentData.order) errors.push('Order ID is required');
    if (!paymentData.payment_method) errors.push('Payment method is required');
    if (['ncba', 'mpesa'].includes(paymentData.payment_method) && !paymentData.phone_number) {
      errors.push('Phone number is required for mobile payments');
    }
    return { isValid: errors.length === 0, errors };
  }

  /**
   * Format payment amount for display
   */
  formatAmount(amount, currency = 'KES') {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  }

  /**
   * Get payment status display info
   */
  getPaymentStatusInfo(status) {
    const statusMap = {
      pending:    { label: 'Pending',    color: 'yellow', description: 'Payment is being processed' },
      processing: { label: 'Processing', color: 'blue',   description: 'Payment is being verified' },
      paid:       { label: 'Paid',       color: 'green',  description: 'Payment completed successfully' },
      failed:     { label: 'Failed',     color: 'red',    description: 'Payment failed' },
      cancelled:  { label: 'Cancelled',  color: 'gray',   description: 'Payment was cancelled' },
    };
    return statusMap[status] || { label: status, color: 'gray', description: 'Unknown payment status' };
  }

  /**
   * Handle API errors consistently
   */
  handleError(error, defaultMessage) {
    const message =
      error.response?.data?.message ||
      error.response?.data?.detail ||
      error.message ||
      defaultMessage;
    logger.error('Payment Service Error:', { message, status: error.response?.status, data: error.response?.data });
    return new Error(message);
  }
}

const paymentService = new PaymentService();

export const {
  initiatePayment,
  getPaymentStatus,
  checkPaymentStatus,
  processPayment,
  generateNCBAQR,
  validatePaymentData,
  formatAmount,
  getPaymentStatusInfo,
} = paymentService;

export default paymentService;
